/**
 * Zod input schemas: projects. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { z } from 'zod';
import { PROJECT_CATEGORIES, PROJECT_PHASES, PROJECT_STATUSES, PROJECT_HEALTH, PROJECT_PRIORITIES, DELIVERY_READINESS, DECISION_SCOPING, DISCOVERY_STATUSES, EVAL_REQUIRED, EVAL_FOCUS, ALREADY_FACTORED_IN, WORKFLOW_ADJUSTMENTS, ACTION_ITEM_STATUSES, MILESTONE_STATUSES, } from '../productProjects.js';
/** Nullable optional short text (up to 2000 chars; blank → null). */
const optProjText = (max = 2000) => z
    .union([z.string().trim().max(max), z.null(), z.undefined()])
    .transform((v) => (v == null || v === '' ? null : v));
/** One sprint in a project's execution plan. */
const ProjectSprintSchema = z
    .object({
    id: z.string().trim().min(1).max(60),
    name: z.string().trim().min(1).max(120),
    startDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/),
    endDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/),
})
    .refine((s) => s.endDate > s.startDate, {
    message: 'Sprint end date must be after the start date',
});
/** One role/expectation row. */
const ProjectRoleExpectationSchema = z.object({
    partner: z.string().trim().max(120).default(''),
    role: z.string().trim().max(200).default(''),
    deliverable: z.string().trim().max(200).default(''),
});
/** One planned phase window (each side independently clearable). */
const ProjectPhaseWindowSchema = z.object({
    startDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).nullish(),
    endDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).nullish(),
});
/** One milestone embedded in a project. */
const ProjectMilestoneSchema = z.object({
    id: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(200),
    ownerEmail: z.string().trim().toLowerCase().email().nullable().optional(),
    targetDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    status: z.enum(MILESTONE_STATUSES),
});
/** One typed project link. URL must be http(s); label is nullish (the Firebase
 *  serializer turns undefined into null, so .optional() alone would reject it). */
const ProjectLinkSchema = z.object({
    type: z.string().trim().min(1).max(60),
    url: z
        .string()
        .trim()
        .min(1)
        .max(1000)
        .refine((v) => v.startsWith('http://') || v.startsWith('https://'), {
        message: 'Link URL must start with http:// or https://',
    }),
    label: z.string().trim().max(200).nullish(),
});
/** One evaluation-plan row. */
const ProjectEvalPlanRowSchema = z.object({
    question: z.string().trim().max(300).default(''),
    method: z.string().trim().max(200).default(''),
    source: z.string().trim().max(200).default(''),
    owner: z.string().trim().max(120).nullable().optional(),
    timing: z.string().trim().max(120).nullable().optional(),
});
/** One dependency row. */
const ProjectDependencySchema = z.object({
    name: z.string().trim().max(120).default(''),
    owner: z.string().trim().max(120).default(''),
    neededBefore: z.string().trim().max(120).default(''),
    status: z.string().trim().max(120).default(''),
});
/**
 * Create or update a product project.  All fields are optional on update
 * (server applies a partial merge), but `name`, `projectId`, and
 * `ownerEmail` are required on create (validated server-side).
 */
/** Create the project's private Slack channel (PM / Product admin only). */
export const ProjectSlackCreateSchema = z.object({
    projectId: z.string().trim().min(1).max(80),
});
/** Extract action items from a Google Meet notes/transcript doc (participants). */
export const ProjectExtractMeetingSchema = z.object({
    projectId: z.string().trim().min(1).max(80),
    docUrl: z.string().trim().url().max(500),
});
/** Approve/dismiss a pending Slack suggestion (PM / Product admin only). */
export const ProjectSuggestionDecideSchema = z.object({
    projectId: z.string().trim().min(1).max(80),
    suggestionId: z.string().trim().min(1).max(120),
    decision: z.enum(['approve', 'dismiss']),
});
/** Post a manual note into a project's activity timeline. kind lets the poster
 *  mark the entry (Note default; Decision/Meeting markers) — httpsCallable
 *  serializes undefined→null, so these MUST stay .nullish(), not .optional(). */
export const ProjectActivityNoteSchema = z.object({
    projectId: z.string().trim().min(1).max(80),
    text: z.string().trim().min(1).max(2000),
    kind: z.enum(['note', 'decision', 'meeting']).nullish(),
    meetingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullish(),
});
/** Fetch the Linear snapshot for a project (participant/admin). */
export const ProjectLinearSyncSchema = z.object({
    projectId: z.string().trim().min(1).max(80),
});
/** Transcript import (two-popup flow): extract Decisions + Next Steps from a
 *  Meet transcript — either a Google Doc URL (read as the caller via DWD) or
 *  pasted/uploaded raw text. Exactly one of docUrl/text is required (checked
 *  server-side; .nullish() for callable serialization). */
export const ProjectTranscriptExtractSchema = z.object({
    projectId: z.string().trim().min(1).max(80),
    docUrl: z.string().trim().url().max(500).nullish(),
    text: z.string().max(120_000).nullish(),
});
/** Save the loader's SELECTED decisions + next steps to the project. Every
 *  next step must carry an assignee and a due date (the popup enforces adding
 *  one when the transcript had none). */
export const ProjectTranscriptSaveSchema = z.object({
    projectId: z.string().trim().min(1).max(80),
    meetingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    sourceRef: z.string().trim().max(200).nullish(),
    decisions: z.array(z.string().trim().min(1).max(600)).max(30),
    nextSteps: z
        .array(z.object({
        title: z.string().trim().min(1).max(140),
        assigneeEmail: z.string().trim().toLowerCase().email(),
        dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    }))
        .max(25),
});
export const ProjectSaveSchema = z.object({
    /** Provided on UPDATE; absent on CREATE (server assigns). */
    id: z.string().trim().min(1).max(60).optional(),
    // Identity
    projectId: z.string().trim().min(1).max(60).optional(),
    name: z.string().trim().min(1).max(200).optional(),
    category: z.enum(PROJECT_CATEGORIES).optional(),
    descriptionShort: optProjText(500).optional(),
    // Ownership — nullable: pre-acceptance projects have no PM yet, and the
    // full-edit save spreads the whole doc (incl. ownerEmail: null) back in.
    ownerEmail: z.string().trim().toLowerCase().email().nullable().optional(),
    counterpart: optProjText(200).optional(),
    counterparts: z.array(z.object({
        email: z.string().trim().toLowerCase().email(),
        name: z.string().trim().max(200),
    })).max(20).optional(),
    teams: z.array(z.string().trim().max(120)).max(20).optional(),
    // Purpose
    problem: optProjText().optional(),
    targetAudience: optProjText().optional(),
    goal: optProjText().optional(),
    successMetric: optProjText().optional(),
    outOfScope: optProjText().optional(),
    // Pre-planning
    decisionScopingRequired: z.enum(DECISION_SCOPING).optional(),
    involved: optProjText().optional(),
    discoveryStatus: z.enum(DISCOVERY_STATUSES).optional(),
    mustHaves: optProjText().optional(),
    designRefs: optProjText().optional(),
    // Structured arrays
    rolesExpectations: z.array(ProjectRoleExpectationSchema).max(50).optional(),
    dependencies: z.array(ProjectDependencySchema).max(50).optional(),
    milestones: z.array(ProjectMilestoneSchema).max(100).optional(),
    evalPlan: z.array(ProjectEvalPlanRowSchema).max(50).optional(),
    // Post-effects
    affected: optProjText().optional(),
    workflowAdjustments: z.enum(WORKFLOW_ADJUSTMENTS).optional(),
    changeOwner: optProjText(200).optional(),
    processChanges: optProjText().optional(),
    gtmNeeds: optProjText().optional(),
    // Evaluation
    evalRequired: z.enum(EVAL_REQUIRED).optional(),
    evalFocus: z.enum(EVAL_FOCUS).nullable().optional(),
    alreadyFactoredIn: z.enum(ALREADY_FACTORED_IN).optional(),
    reviewTiming: optProjText(200).optional(),
    // Readiness gate
    deliveryReadiness: z.enum(DELIVERY_READINESS).optional(),
    readinessBlockers: optProjText().optional(),
    // Portfolio control
    phase: z.enum(PROJECT_PHASES).optional(),
    status: z.enum(PROJECT_STATUSES).optional(),
    health: z.enum(PROJECT_HEALTH).optional(),
    priority: z.enum(PROJECT_PRIORITIES).optional(),
    startDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    targetDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    endDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    progressPct: z.number().int().min(0).max(100).optional(),
    // Execution planning — nullish (NOT bare .optional()): the Firebase
    // serializer sends undefined as null on the wire.
    pi: optProjText(40).optional(),
    sprints: z.array(ProjectSprintSchema).max(30).nullish(),
    phasePlan: z
        .object({
        Scoping: ProjectPhaseWindowSchema.nullish(),
        Planning: ProjectPhaseWindowSchema.nullish(),
        Execution: ProjectPhaseWindowSchema.nullish(),
        Rollout: ProjectPhaseWindowSchema.nullish(),
    })
        .nullish(),
    // Run & update
    nextStep: optProjText().optional(),
    mainRisk: optProjText().optional(),
    latestUpdate: optProjText().optional(),
    slackChannel: optProjText(200).optional(),
    docLink: optProjText(500).optional(),
    folderLink: optProjText(500).optional(),
    /** Linear project URL (read-only bridge; slug parsed + fetched server-side). */
    linearProjectUrl: optProjText(300).optional(),
    /** Typed links — the type is NOT validated against config (a type removed
     *  from config later must not break saves); the URL scheme is enforced. */
    links: z.array(ProjectLinkSchema).max(50).optional(),
    /** Product admins may set this on CREATE to skip intake (Accepted immediately). */
    directCreate: z.boolean().optional(),
});
/** Delete a project (also cascades its action items). */
export const ProjectDeleteSchema = z.object({
    id: z.string().trim().min(1).max(60),
});
/** Create or update a project action item. */
export const ActionItemSaveSchema = z.object({
    /** Provided on UPDATE; absent on CREATE (server assigns). */
    id: z.string().trim().min(1).max(60).optional(),
    projectId: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(300),
    details: optProjText(),
    assigneeEmail: z.string().trim().toLowerCase().email(),
    dueDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    priority: z.enum(PROJECT_PRIORITIES).optional(),
    status: z.enum(ACTION_ITEM_STATUSES).optional(),
});
/** Transition an action item to Open / Blocked / Done. */
export const ActionItemSetStatusSchema = z.object({
    id: z.string().trim().min(1).max(60),
    status: z.enum(ACTION_ITEM_STATUSES),
});
/** Submit a project request (Draft/Returned → Submitted). */
export const ProjectSubmitRequestSchema = z.object({
    id: z.string().trim().min(1).max(60),
});
/** Accept or return a submitted project request (Product admins only). */
export const ProjectDecideRequestSchema = z.object({
    id: z.string().trim().min(1).max(60),
    decision: z.enum(['accept', 'return']),
    /** Required on return. */
    note: z.string().trim().max(2000).optional(),
    /** On accept: assign to a specific Product Manager (required on accept). Defaults to caller when absent. */
    ownerEmail: z.string().trim().toLowerCase().email().optional(),
    /** On accept: initial counterparts (optional). */
    counterparts: z.array(z.object({
        email: z.string().trim().toLowerCase().email(),
        name: z.string().trim().max(200),
    })).max(20).optional(),
});
/** Approve a scope change on an Accepted project. */
export const ProjectReviewScopeChangeSchema = z.object({
    id: z.string().trim().min(1).max(60),
    changeId: z.string().trim().min(1).max(60),
    note: z.string().trim().max(2000).optional(),
});
/** Replace config.projectLinkTypes (Projects tool-admins, via callable — they
 *  are not app admins so cannot updateDoc /config/app). Non-empty unique
 *  trimmed strings, cap 30 (uniqueness re-checked case-insensitively server-side). */
export const ProjectSetLinkTypesSchema = z.object({
    types: z.array(z.string().trim().min(1).max(60)).min(1).max(30),
});
//# sourceMappingURL=projects.js.map